import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getQueryParam } from 'src/lib/utils';
import { RootState } from '../../features';
import { actions, UserErrorType } from '../../features/user';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { UserList } from './UserList';

const playerSelectStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px 0;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);

  main {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-width: 480px;
    max-height: 720px;

    p {
      color: #4d3906;
    }
  }
  .description {
    padding: 0 24px;
  }
  .user-list {
    overflow: auto;
    flex: 1 1 auto;
    margin: 24px 0 0;
  }
  .button {
    flex: 0 0 auto;
    padding: 8px 0;
  }
  .error {
    font-size: 12px;
    text-align: center;
    color: #e60012;
    padding: 1em 0;
  }
  .notice {
    font-size: 12px;
    text-align: center;
    color: #938666;
  }
  .notice.first {
    margin: 12px 0 0;
  }
  .notice.second {
    margin: 8px 0 20px;
  }
  .bottom-container {
    padding: 0 24px;
  }
`;

const PlayerSelect: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'settings']);
  const history = useHistory();
  const { users, errorType, selectStatus, marked } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const raid = getQueryParam(location.search, 'raid');

  const [checkedUserId, setCheckedUserId] = useState<string | undefined>(undefined);
  const error = getText(t, errorType);
  const handleConfirmClick = useCallback(() => {
    if (checkedUserId) {
      dispatch(actions.selectUser({ selectedUserId: checkedUserId, language: i18n.language }));
    }
  }, [checkedUserId, i18n.language, dispatch]);
  const handleUserClick = useCallback((userId: string) => () => setCheckedUserId(userId), [
    setCheckedUserId
  ]);

  const isLoading = selectStatus === 'loading';

  useEffect(() => {
    if (selectStatus === 'succeeded') {
      const walkthroughPath = raid ? `/walkthrough/1?raid=${raid}` : '/walkthrough/1';
      const dest = marked ? '/players/home' : walkthroughPath;
      history.replace(dest);
    }
  }, [selectStatus, marked, history, raid]);

  return (
    <div className={playerSelectStyle}>
      <main>
        <p className="description">{t('select_player')}</p>
        <div className="user-list">
          <UserList {...{ users, disabled: isLoading, checkedUserId, handleUserClick }} />
        </div>
        <div className="bottom-container">
          {errorType && <p className="error">{error}</p>}
          <div className="button">
            <ConfirmButton
              disabled={!checkedUserId}
              loading={selectStatus === 'loading'}
              onClick={handleConfirmClick}
            >
              {t('dialog_button_ok')}
            </ConfirmButton>
          </div>
          <p className="notice first">{t('select_player_01')}</p>
          <p className="notice second">{t('settings:reselect_user_01')}</p>
        </div>
      </main>
    </div>
  );
};

export default PlayerSelect;

function getText(t: TFunction, errorType?: UserErrorType): string {
  switch (errorType) {
    case 'player-profile-not-found':
      return t('translation:error_player_profile_not_found');
    case 'player-not-in-island':
      return t('error_player_not_in_island');
    case 'unstable-network':
      return t('error_unstable_network');
    case 'unknown':
    default:
      return t('error_unknown');
  }
}
